import React from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AddBusinessRoundedIcon from "@mui/icons-material/AddBusinessRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import { activityApi } from "../../api/activity";
import { billingAccessLabel, getProjectBillingAccess } from "../../utils/projectBillingAccess";
import {
  PROJECT_BUSINESS_GOALS,
  isProjectCapabilityEnabled,
  projectBusinessGoals,
  projectBusinessProfileDefinition,
  projectStarterAreas,
} from "../../utils/projectCapabilities";
import { getProjectPublicUrl } from "../../utils/projectDeployment";
import { buildProjectActionTasks, buildProjectRoadmap } from "../../utils/projectActionTasks";
import { getProjectExperience } from "../../utils/projectExperience";
import { normalizeProjectModules } from "../../utils/projectModules";
import ContextualHelp from "./ContextualHelp";

const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  purple: "#6957E8",
  green: "#26A269",
  blue: "#2878D4",
  yellow: "#FFC857",
};

const COLOR_MAP = {
  orange: { main: BRAND.orange, soft: "rgba(243,90,58,.11)", border: "rgba(243,90,58,.24)" },
  purple: { main: BRAND.purple, soft: "rgba(105,87,232,.10)", border: "rgba(105,87,232,.22)" },
  green: { main: BRAND.green, soft: "rgba(38,162,105,.10)", border: "rgba(38,162,105,.22)" },
  blue: { main: BRAND.blue, soft: "rgba(40,120,212,.10)", border: "rgba(40,120,212,.22)" },
  yellow: { main: "#9A6500", soft: "rgba(255,200,87,.18)", border: "rgba(154,101,0,.2)" },
  dark: { main: BRAND.dark, soft: "rgba(25,36,47,.08)", border: "rgba(25,36,47,.16)" },
};

function ActionTile({
  icon,
  title,
  description,
  label = "Abrir",
  color = "orange",
  disabled = false,
  badge,
  featured = false,
  onClick,
}) {
  const palette = COLOR_MAP[color] || COLOR_MAP.orange;

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`${title}: ${disabled ? "ver acceso" : label}`}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: { xs: 48, sm: 0 },
        display: "block",
        textAlign: "left",
        borderRadius: { xs: 2.5, sm: 3.4 },
        overflow: "hidden",
        touchAction: "manipulation",
        "&:focus-visible": { boxShadow: `0 0 0 4px ${palette.soft}` },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          minHeight: featured ? { xs: 82, sm: 168 } : { xs: 72, sm: 142 },
          borderRadius: { xs: 2.5, sm: 3.4 },
          border: `1px solid ${disabled ? "rgba(25,36,47,.1)" : palette.border}`,
          background: featured && !disabled
            ? `linear-gradient(145deg, #fff 0%, ${palette.soft} 100%)`
            : disabled
              ? "rgba(25,36,47,.025)"
              : "#fff",
          p: featured ? { xs: 1.1, sm: 2.2 } : { xs: 1, sm: 1.75 },
          opacity: disabled ? .78 : 1,
          display: "grid",
          gridTemplateColumns: { xs: "auto minmax(0, 1fr) auto", sm: "auto minmax(0, 1fr)" },
          gridTemplateRows: { xs: "1fr", sm: "auto 1fr" },
          gridTemplateAreas: { xs: "'icon content trail'", sm: "'icon trail' 'content content'" },
          alignItems: { xs: "center", sm: "start" },
          columnGap: { xs: 1, sm: 1.15 },
          rowGap: { xs: 0, sm: 1.1 },
          transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
          "&:hover": {
            transform: { xs: "none", sm: "translateY(-3px)" },
            boxShadow: "0 15px 34px rgba(25,36,47,.1)",
            borderColor: disabled ? "rgba(25,36,47,.2)" : palette.main,
          },
        }}
      >
        <Box
          sx={{
            gridArea: "icon",
            width: featured ? { xs: 40, sm: 48 } : { xs: 38, sm: 43 },
            height: featured ? { xs: 40, sm: 48 } : { xs: 38, sm: 43 },
            borderRadius: { xs: 2.1, sm: 2.7 },
            display: "grid",
            placeItems: "center",
            color: disabled ? "rgba(25,36,47,.5)" : palette.main,
            bgcolor: disabled ? "rgba(25,36,47,.07)" : palette.soft,
            flexShrink: 0,
            "& svg": { fontSize: featured ? { xs: 22, sm: 26 } : { xs: 21, sm: 23 } },
          }}
        >
          {disabled ? <LockClockRoundedIcon /> : icon}
        </Box>

        <Box sx={{ gridArea: "trail", justifySelf: "end", alignSelf: { xs: "center", sm: "start" }, pl: .35 }}>
          {badge ? (
            <Chip
              size="small"
              label={badge}
              sx={{
                height: 22,
                maxWidth: { xs: 96, sm: 150 },
                fontSize: ".61rem",
                fontWeight: 950,
                bgcolor: disabled ? "rgba(25,36,47,.06)" : palette.soft,
                color: disabled ? "rgba(25,36,47,.58)" : palette.main,
              }}
            />
          ) : (
            <ArrowForwardRoundedIcon sx={{ color: disabled ? "rgba(25,36,47,.35)" : palette.main, fontSize: { xs: 20, sm: 21 } }} />
          )}
        </Box>

        <Box sx={{ gridArea: "content", minWidth: 0, alignSelf: "center" }}>
          <Typography
            sx={{
              color: BRAND.dark,
              fontSize: featured ? { xs: ".9rem", sm: "1.08rem" } : { xs: ".82rem", sm: ".96rem" },
              lineHeight: 1.16,
              fontWeight: 1000,
              letterSpacing: "-.15px",
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              display: { xs: featured ? "-webkit-box" : "none", sm: "block" },
              mt: { xs: .25, sm: .48 },
              color: "rgba(25,36,47,.6)",
              fontSize: featured ? { xs: ".68rem", sm: ".8rem" } : ".75rem",
              lineHeight: 1.34,
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: { xs: 1, sm: "unset" },
              overflow: { xs: "hidden", sm: "visible" },
            }}
          >
            {description}
          </Typography>
          <Typography
            sx={{
              mt: { xs: .28, sm: .75 },
              color: disabled ? "rgba(25,36,47,.52)" : palette.main,
              fontSize: { xs: ".65rem", sm: ".72rem" },
              fontWeight: 1000,
            }}
          >
            {disabled ? "Ver acceso" : label}
          </Typography>
        </Box>
      </Paper>
    </ButtonBase>
  );
}

function MobilePriorityAction({ action }) {
  if (!action) return null;
  const palette = COLOR_MAP[action.color] || COLOR_MAP.orange;

  return (
    <Paper
      elevation={0}
      sx={{
        display: { xs: "block", sm: "none" },
        borderRadius: 3,
        border: `1px solid ${palette.border}`,
        background: `linear-gradient(145deg, #fff 0%, ${palette.soft} 100%)`,
        p: 1.2,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.2,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            bgcolor: "#fff",
            color: action.locked ? "rgba(25,36,47,.48)" : palette.main,
            "& svg": { fontSize: 21 },
          }}
        >
          {action.locked ? <LockClockRoundedIcon /> : action.icon || <TaskAltRoundedIcon />}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: "rgba(25,36,47,.5)", fontSize: ".61rem", fontWeight: 1000, letterSpacing: .55 }}>
            SIGUIENTE ACCIÓN
          </Typography>
          <Typography sx={{ mt: .15, color: BRAND.dark, fontSize: ".96rem", lineHeight: 1.15, fontWeight: 1000 }}>
            {action.title}
          </Typography>
          {action.description ? (
            <Typography
              sx={{
                mt: .3,
                color: "rgba(25,36,47,.58)",
                fontSize: ".7rem",
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
              }}
            >
              {action.description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      <Button
        fullWidth
        variant="contained"
        endIcon={action.locked ? <LockClockRoundedIcon /> : <ArrowForwardRoundedIcon />}
        onClick={action.onClick}
        sx={{
          mt: 1,
          minHeight: 46,
          borderRadius: 2.4,
          textTransform: "none",
          fontWeight: 1000,
          bgcolor: action.locked ? BRAND.dark : palette.main,
          "&:hover": { bgcolor: action.locked ? BRAND.dark : palette.main, filter: "brightness(.94)" },
        }}
      >
        {action.locked ? "Ver acceso" : action.actionLabel || "Continuar"}
      </Button>
    </Paper>
  );
}

const OPERATIONAL_ROUTE_SEGMENTS = ["/sistema", "/clientes", "/productos", "/agenda", "/pedidos", "/mesas"];

function isOperationalDestination(path = "") {
  return OPERATIONAL_ROUTE_SEGMENTS.some((segment) => String(path).includes(segment));
}

function projectIdentifier(project) {
  return project?.slug || project?.id || project?._id || "";
}

function relativeTime(value) {
  const timestamp = new Date(value || 0).getTime();
  if (!Number.isFinite(timestamp) || timestamp <= 0) return "";
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60000));
  if (minutes < 1) return "Ahora";
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} d`;
}

function DashboardMetric({ label, value, helper, icon }) {
  return (
    <Paper elevation={0} sx={{ height: "100%", p: { xs: 1.2, sm: 1.6 }, borderRadius: 3, border: "1px solid rgba(25,36,47,.08)", bgcolor: "#fff" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: "rgba(25,36,47,.52)", fontSize: ".68rem", fontWeight: 900 }}>{label}</Typography>
          <Typography sx={{ mt: .25, color: BRAND.dark, fontSize: { xs: "1.22rem", sm: "1.5rem" }, lineHeight: 1, fontWeight: 1000 }}>{value}</Typography>
          <Typography sx={{ display: { xs: "none", sm: "block" }, mt: .45, color: "rgba(25,36,47,.5)", fontSize: ".67rem", lineHeight: 1.3 }}>{helper}</Typography>
        </Box>
        <Box sx={{ width: 34, height: 34, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "rgba(105,87,232,.08)", color: BRAND.purple, flexShrink: 0 }}>
          {icon}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function ClientCommandCenter({
  user,
  projects,
  activeProject,
  setActiveProject,
  projectCreation,
  loading,
  error,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showAllTools, setShowAllTools] = React.useState(false);
  const [showGuidance, setShowGuidance] = React.useState(false);
  const [readiness, setReadiness] = React.useState(null);
  const [readinessLoading, setReadinessLoading] = React.useState(false);
  const [activitySummary, setActivitySummary] = React.useState(null);
  const [recentActivity, setRecentActivity] = React.useState([]);
  const [activityLoading, setActivityLoading] = React.useState(false);
  const modules = normalizeProjectModules(activeProject?.modules);
  const billingAccess = getProjectBillingAccess(activeProject);
  const projectKey = projectIdentifier(activeProject);
  const encodedProjectKey = encodeURIComponent(projectKey);
  const projectBase = projectKey
    ? `/dashboard/proyectos/${encodedProjectKey}`
    : "/dashboard/proyectos";
  const publicUrl = getProjectPublicUrl(activeProject);
  const operationallyLocked = billingAccess.isLandingOnly || billingAccess.isSuspended;
  const membershipsEnabled = isProjectCapabilityEnabled(activeProject, "memberships");
  const itemPath = modules.system ? `${projectBase}/sistema/items` : `${projectBase}/productos`;
  const subscribePath = `${projectBase}/configuracion/suscripcion`;
  const experience = getProjectExperience(activeProject, readiness ?? activeProject?.readiness);
  const guidedStart = experience.intensiveOnboarding;
  const businessGoals = projectBusinessGoals(activeProject);
  const businessProfile = projectBusinessProfileDefinition(activeProject?.businessProfile);
  const businessGoalLabels = PROJECT_BUSINESS_GOALS
    .filter((goal) => businessGoals.includes(goal.key))
    .map((goal) => goal.label);
  const starterAreas = projectStarterAreas(activeProject);
  const firstProjectEntry = searchParams.get("entrada") === "primer-proyecto";
  const tablesEnabled = isProjectCapabilityEnabled(activeProject, "tables");
  const tableReservationsEnabled = isProjectCapabilityEnabled(activeProject, "tableReservations");
  const kitchenOrdersEnabled = isProjectCapabilityEnabled(activeProject, "kitchenOrders");
  const gastronomicMenuEnabled = isProjectCapabilityEnabled(activeProject, "gastronomicMenu");
  const recipesEnabled = isProjectCapabilityEnabled(activeProject, "recipes");

  React.useEffect(() => {
    setShowAllTools(false);
    setShowGuidance(false);
  }, [projectKey]);

  React.useEffect(() => {
    let cancelled = false;
    const fallback = activeProject?.readiness || null;
    setReadiness(fallback);
    setActivitySummary(null);
    setRecentActivity([]);

    if (!projectKey) {
      setReadinessLoading(false);
      setActivityLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setReadinessLoading(true);
    setActivityLoading(true);

    Promise.allSettled([
      projectsApi.getReadiness(projectKey),
      activityApi.projectSummary(projectKey, 168),
      activityApi.project(projectKey, { page: 1, pageSize: 5 }),
    ]).then(([readinessResult, summaryResult, activityResult]) => {
      if (cancelled) return;

      setReadiness(
        readinessResult.status === "fulfilled"
          ? readinessResult.value?.readiness || fallback
          : fallback,
      );
      setActivitySummary(
        summaryResult.status === "fulfilled"
          ? summaryResult.value?.summary || null
          : null,
      );
      setRecentActivity(
        activityResult.status === "fulfilled" && Array.isArray(activityResult.value?.items)
          ? activityResult.value.items
          : [],
      );
    }).finally(() => {
      if (!cancelled) {
        setReadinessLoading(false);
        setActivityLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [projectKey, activeProject?.readiness?.computedAt]);

  const go = React.useCallback((path, { operational = false } = {}) => {
    if (!projectKey) {
      navigate("/dashboard/proyectos");
      return;
    }
    if (operational && operationallyLocked) {
      navigate(subscribePath);
      return;
    }
    navigate(path);
  }, [navigate, operationallyLocked, projectKey, subscribePath]);

  const openPublicSite = () => {
    if (publicUrl) {
      window.open(publicUrl, "_blank", "noopener,noreferrer");
      return;
    }
    go(`${projectBase}/pagina`);
  };

  if (loading && !activeProject) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 4, p: 3, border: "1px solid rgba(25,36,47,.08)" }}>
        <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>Preparando tu panel...</Typography>
        <LinearProgress sx={{ mt: 2, borderRadius: 99 }} />
      </Paper>
    );
  }

  if (!activeProject) {
    return (
      <Stack spacing={{ xs: 1.5, sm: 2.25 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            p: { xs: 2, sm: 3.25 },
            border: "1px solid rgba(25,36,47,.08)",
            background: "linear-gradient(135deg, #fff 0%, rgba(243,90,58,.035) 100%)",
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: BRAND.dark, fontWeight: 1000, letterSpacing: { xs: "-.35px", sm: "-.6px" } }}
          >
            ¿Cómo querés empezar?
          </Typography>
          <Typography sx={{ mt: .7, color: "rgba(25,36,47,.62)", maxWidth: 760 }}>
            Tu cuenta ya está lista. Creá tu negocio o abrí tu Red de referidos; el enlace personal no requiere un proyecto.
          </Typography>

          <Grid container spacing={{ xs: 1.25, sm: 1.75 }} sx={{ mt: { xs: .6, sm: 1 } }}>
            <Grid item xs={12} md={6}>
              <ActionTile
                featured
                icon={<StorefrontRoundedIcon />}
                title="Crear mi negocio"
                description="Empezá tu primer proyecto con el nombre del negocio y completá el resto después."
                label={projectCreation?.canCreate === false ? "Ver proyectos" : "Crear negocio"}
                color="orange"
                onClick={() => navigate("/dashboard/proyectos")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ActionTile
                featured
                icon={<ShareRoundedIcon />}
                title="Trabajar con WebProfit"
                description="Abrí tu Red de referidos, copiá tu enlace personal y empezá a sumar clientes sin crear un proyecto propio."
                label="Abrir Red de referidos"
                color="purple"
                onClick={() => navigate("/dashboard/referidos")}
              />
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    );
  }

  const membershipActions = membershipsEnabled ? [
    {
      key: "membership-customer",
      icon: <PersonAddAlt1RoundedIcon />,
      title: "Crear cliente",
      description: "Alta rápida en CRM, sin obligar a crear contraseña ni acceso web.",
      label: "Nuevo cliente",
      color: "orange",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes?accion=crear-cliente`, { operational: true }),
    },
    {
      key: "membership-assign",
      icon: <CardMembershipRoundedIcon />,
      title: "Asignar membresía",
      description: "Buscá un cliente y vinculale un servicio o ítem recurrente con vigencia e importe.",
      label: "Ir al CRM",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes?vista=membresias&accion=asignar-plan`, { operational: true }),
    },
    {
      key: "membership-renew",
      icon: <AutorenewRoundedIcon />,
      title: "Renovar membresía",
      description: "Accedé a los períodos que requieren continuidad sin perder historial.",
      label: "Revisar renovaciones",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes?vista=membresias&accion=renovar`, { operational: true }),
    },
    {
      key: "membership-expirations",
      icon: <NotificationsActiveRoundedIcon />,
      title: "Revisar vencimientos",
      description: "Priorizá próximas a vencer y vencidas desde el seguimiento del CRM.",
      label: "Ver vencimientos",
      color: "yellow",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes?vista=vencimientos&accion=vencimientos`, { operational: true }),
    },
  ] : [];

  const primaryActions = [
    {
      key: "landing",
      visible: modules.landing,
      icon: <DesignServicesRoundedIcon />,
      title: "Editar página",
      description: "Cambiá portada, textos, imágenes, colores y datos de contacto.",
      label: "Abrir editor",
      color: "orange",
      onClick: () => go(`${projectBase}/pagina`),
    },
    {
      key: "product",
      visible: modules.products || modules.system,
      icon: <Inventory2RoundedIcon />,
      title: "Cargar producto",
      description: "Creá un artículo con precio, imágenes y stock.",
      label: "Nuevo producto",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${itemPath}?crear=1&tipo=product`, { operational: true }),
    },
    {
      key: "service",
      visible: modules.products || modules.system,
      icon: <AddBusinessRoundedIcon />,
      title: "Cargar servicio",
      description: "Publicá una consulta, turno o trabajo con duración y modalidad.",
      label: "Nuevo servicio",
      color: "blue",
      disabled: operationallyLocked,
      onClick: () => go(`${itemPath}?crear=1&tipo=service`, { operational: true }),
    },
    {
      key: "movement",
      visible: modules.system,
      icon: <PointOfSaleRoundedIcon />,
      title: "Nueva operación",
      description: "Registrá una venta, compra, cobro, pago, ingreso o gasto.",
      label: "Generar operación",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/sistema/movimientos?nuevo=1`, { operational: true }),
    },
  ].filter((action) => action.visible);

  const serviceFirst =
    businessGoals.includes("offerServices") ||
    (["services", "gym"].includes(activeProject?.businessProfile) && !businessGoals.includes("sellProducts"));

  const guidedActionByArea = {
    landing: {
      key: "guided-landing",
      visible: modules.landing,
      icon: <DesignServicesRoundedIcon />,
      title: "Preparar mi página",
      description: "Completá los datos básicos y dejá lista la presentación del negocio.",
      result: "Una presentación pública con la identidad y los datos principales de tu negocio.",
      label: "Editar página",
      color: "orange",
      onClick: () => go(`${projectBase}/pagina`),
    },
    items: {
      key: "guided-items",
      visible: modules.products || modules.system,
      icon: serviceFirst ? <AddBusinessRoundedIcon /> : <Inventory2RoundedIcon />,
      title: serviceFirst ? "Cargar un servicio" : "Cargar un producto",
      description: serviceFirst
        ? "Empezá por el servicio que querés ofrecer o agendar."
        : "Empezá por un artículo real con precio e información básica.",
      result: serviceFirst
        ? "Tu primer servicio real, listo para mostrar, agendar o gestionar."
        : "Tu primer producto real, listo para mostrar en el catálogo y empezar a vender.",
      label: serviceFirst ? "Nuevo servicio" : "Nuevo producto",
      color: serviceFirst ? "blue" : "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${itemPath}?crear=1&tipo=${serviceFirst ? "service" : "product"}`, { operational: true }),
    },
    agenda: {
      key: "guided-agenda",
      visible: modules.products || tablesEnabled || tableReservationsEnabled,
      icon: <EventAvailableRoundedIcon />,
      title: tablesEnabled || tableReservationsEnabled ? "Preparar mesas y reservas" : "Preparar agenda",
      description: tablesEnabled || tableReservationsEnabled
        ? "Organizá el salón y dejá listo el circuito de reservas de mesa."
        : "Definí disponibilidad y empezá a ordenar reservas o turnos.",
      result: tablesEnabled || tableReservationsEnabled
        ? "Tus mesas y reservas preparadas para empezar a recibir clientes."
        : "Una agenda con disponibilidad lista para empezar a recibir turnos o reservas.",
      label: tablesEnabled || tableReservationsEnabled ? "Abrir mesas" : "Abrir agenda",
      color: tablesEnabled || tableReservationsEnabled ? "orange" : "blue",
      disabled: operationallyLocked,
      onClick: () => go(
        tablesEnabled || tableReservationsEnabled ? `${projectBase}/mesas` : `${projectBase}/agenda`,
        { operational: true },
      ),
    },
    tables: {
      key: "guided-tables",
      visible: tablesEnabled || tableReservationsEnabled,
      icon: <EventAvailableRoundedIcon />,
      title: tableReservationsEnabled ? "Preparar mesas y reservas" : "Preparar mesas",
      description: tableReservationsEnabled
        ? "Organizá el salón y dejá listo el circuito de reservas de mesa."
        : "Configurá las mesas que vas a usar en la operación del salón.",
      result: tableReservationsEnabled
        ? "Tus mesas y reservas preparadas para empezar a recibir clientes."
        : "El salón configurado con las mesas que vas a usar en la operación.",
      label: "Abrir mesas",
      color: "orange",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/mesas`, { operational: true }),
    },
    orders: {
      key: "guided-orders",
      visible: modules.orders,
      icon: <ListAltRoundedIcon />,
      title: "Revisar pedidos",
      description: "Conocé el flujo que vas a usar cuando empiecen a llegar solicitudes.",
      result: "El circuito de pedidos ubicado y listo para recibir las primeras solicitudes.",
      label: "Abrir pedidos",
      color: "orange",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/pedidos`, { operational: true }),
    },
    customers: {
      key: "guided-customers",
      visible: true,
      icon: <GroupRoundedIcon />,
      title: membershipsEnabled ? "Preparar clientes y membresías" : "Preparar clientes",
      description: membershipsEnabled
        ? "Organizá clientes y dejá listo el seguimiento de sus planes."
        : "Centralizá contactos y relaciones del negocio desde el CRM.",
      result: membershipsEnabled
        ? "Tu primer cliente listo para asignarle y seguir una membresía."
        : "Tu primer cliente centralizado en el CRM para empezar el seguimiento comercial.",
      label: "Abrir CRM",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/clientes`, { operational: true }),
    },
    system: {
      key: "guided-system",
      visible: modules.system,
      icon: <WarehouseRoundedIcon />,
      title: "Preparar la gestión",
      description: "Entrá al centro operativo para caja, movimientos, stock y control.",
      result: "La gestión operativa abierta para registrar tu primera operación real y empezar a controlar el negocio.",
      label: "Abrir gestión",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/sistema`, { operational: true }),
    },
    stats: {
      key: "guided-stats",
      visible: modules.stats,
      icon: <AnalyticsRoundedIcon />,
      title: "Ver estadísticas",
      description: "Ubicá desde ahora dónde vas a revisar actividad y resultados.",
      result: "El lugar donde vas a ver visitas, interés y resultados cuando empiece a moverse el negocio.",
      label: "Abrir estadísticas",
      color: "yellow",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/estadisticas`, { operational: true }),
    },
  };

  const guidedActions = starterAreas
    .map((area) => guidedActionByArea[area])
    .filter((action) => action?.visible)
    .slice(0, 3);
  const featuredActions = guidedActions.length ? guidedActions : primaryActions;
  const firstGuidedArea = starterAreas.find(
    (area) => area !== "landing" && guidedActionByArea[area]?.visible,
  ) || starterAreas.find((area) => guidedActionByArea[area]?.visible) || "landing";
  const firstGuidedAction = guidedActionByArea[firstGuidedArea]?.visible
    ? guidedActionByArea[firstGuidedArea]
    : primaryActions[0];

  const readinessAvailable = Number.isFinite(Number(readiness?.requiredCount));
  const projectRoadmap = readinessAvailable
    ? buildProjectRoadmap({ project: activeProject, readiness, modules })
    : [];
  const pendingRoadmapSteps = projectRoadmap.slice(0, 3);
  const hiddenRoadmapCount = Math.max(0, projectRoadmap.length - pendingRoadmapSteps.length);
  const roadmapComplete = readinessAvailable && projectRoadmap.length === 0;
  const keepRoadmapVisible = Boolean(
    activeProject?.onboarding?.guidedStart === true && projectRoadmap.length > 0,
  );
  const operationalTasks = readinessAvailable
    ? buildProjectActionTasks({ project: activeProject, readiness, modules }).slice(0, 3)
    : [];
  const activityMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.total || 0);
  const visitorMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.uniqueVisitors || 0);
  const intentMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.highIntent || 0);
  const teamMetricValue = activityLoading && !activitySummary ? "…" : Number(activitySummary?.uniquePanelUsers || 0);
  const guidanceVisible = readinessAvailable && (guidedStart || showGuidance || keepRoadmapVisible);
  const priorityRoadmapStep = guidanceVisible ? pendingRoadmapSteps[0] : null;
  const priorityOperationalTask = !priorityRoadmapStep ? operationalTasks[0] : null;
  const priorityFeaturedAction = !priorityRoadmapStep && !priorityOperationalTask ? featuredActions[0] : null;
  const mobilePriorityAction = priorityRoadmapStep
    ? {
        title: priorityRoadmapStep.title,
        description: priorityRoadmapStep.description,
        actionLabel: priorityRoadmapStep.label,
        color: priorityRoadmapStep.color,
        locked: priorityRoadmapStep.operational === true && operationallyLocked,
        onClick: () => go(priorityRoadmapStep.path, { operational: priorityRoadmapStep.operational === true }),
      }
    : priorityOperationalTask
      ? {
          title: priorityOperationalTask.label,
          description: priorityOperationalTask.description,
          actionLabel: "Resolver pendiente",
          color: "orange",
          locked: isOperationalDestination(priorityOperationalTask.path) && operationallyLocked,
          onClick: () => go(priorityOperationalTask.path, { operational: isOperationalDestination(priorityOperationalTask.path) }),
        }
      : priorityFeaturedAction
        ? {
            title: priorityFeaturedAction.title,
            description: priorityFeaturedAction.description,
            actionLabel: priorityFeaturedAction.label,
            color: priorityFeaturedAction.color,
            icon: priorityFeaturedAction.icon,
            locked: priorityFeaturedAction.disabled === true,
            onClick: priorityFeaturedAction.onClick,
          }
        : null;

  const managementActions = [
    {
      key: "items",
      visible: modules.products || modules.system,
      icon: <Inventory2RoundedIcon />,
      title: modules.system ? "Ítems del negocio" : "Productos y servicios",
      description: "Revisá, editá y organizá todo lo que ofrecés o utilizás.",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(itemPath, { operational: true }),
    },
    {
      key: "system",
      visible: modules.system,
      icon: <WarehouseRoundedIcon />,
      title: "Gestión del negocio",
      description: "Resumen, caja, movimientos, stock y contabilidad.",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/sistema`, { operational: true }),
    },
    {
      key: "agenda",
      visible: modules.products,
      icon: <EventAvailableRoundedIcon />,
      title: "Agenda y turnos",
      description: "Disponibilidad y reservas de servicios.",
      color: "blue",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/agenda`, { operational: true }),
    },
    {
      key: "orders",
      visible: modules.orders,
      icon: <ListAltRoundedIcon />,
      title: "Pedidos",
      description: "Solicitudes, entregas, retiros y estados de compra.",
      color: "orange",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/pedidos`, { operational: true }),
    },
    {
      key: "stats",
      visible: modules.stats,
      icon: <AnalyticsRoundedIcon />,
      title: "Estadísticas",
      description: "Visitas, recorridos, interés y conversiones.",
      color: "yellow",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/estadisticas`, { operational: true }),
    },
    {
      key: "activity",
      visible: true,
      icon: <HistoryRoundedIcon />,
      title: "Actividad",
      description: "Historial de acciones realizadas en el proyecto.",
      color: "dark",
      onClick: () => go(`${projectBase}/actividad`),
    },
    {
      key: "ecommerce",
      visible: modules.ecommerce,
      icon: <PaymentsRoundedIcon />,
      title: "Cobros online",
      description: "Mercado Pago y checkout de la tienda.",
      color: "green",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/configuracion/ecommerce`, { operational: true }),
    },
    {
      key: "notifications",
      visible: modules.webpush,
      icon: <NotificationsActiveRoundedIcon />,
      title: "Notificaciones",
      description: "Avisos automáticos y campañas web push.",
      color: "purple",
      disabled: operationallyLocked,
      onClick: () => go(`${projectBase}/configuracion/notificaciones`, { operational: true }),
    },
    {
      key: "access",
      visible: true,
      icon: <GroupRoundedIcon />,
      title: "Equipo y accesos",
      description: "Personas, permisos y responsabilidades.",
      color: "dark",
      onClick: () => go(`${projectBase}/configuracion/accesos`),
    },
    {
      key: "settings",
      visible: true,
      icon: <SettingsRoundedIcon />,
      title: "Configuración",
      description: "Datos principales y estructura del proyecto.",
      color: "dark",
      onClick: () => go(`${projectBase}/configuracion/general`),
    },
    {
      key: "subscription",
      visible: true,
      icon: <WorkspacePremiumRoundedIcon />,
      title: "Plan y suscripción",
      description: "Módulos activos y estado del servicio.",
      color: "orange",
      onClick: () => go(subscribePath),
    },
    {
      key: "projects",
      visible: true,
      icon: <StorefrontRoundedIcon />,
      title: "Mis proyectos",
      description: "Cambiá de proyecto o creá uno nuevo.",
      color: "dark",
      onClick: () => navigate("/dashboard/proyectos"),
    },
    {
      key: "public",
      visible: modules.landing,
      icon: <PublicRoundedIcon />,
      title: "Ver página pública",
      description: publicUrl
        ? "Abrí la página que ya pueden visitar tus clientes."
        : "Terminá la publicación para obtener tu enlace.",
      label: publicUrl ? "Abrir web" : "Preparar web",
      color: "blue",
      badge: publicUrl ? "PUBLICADA" : "PENDIENTE",
      onClick: openPublicSite,
    },
  ].filter((action) => action.visible);

  if (firstProjectEntry && firstGuidedAction) {
    const firstPalette = COLOR_MAP[firstGuidedAction.color] || COLOR_MAP.orange;

    return (
      <Stack spacing={{ xs: 1.25, sm: 1.8 }} sx={{ minWidth: 0, maxWidth: 980, mx: "auto" }}>
        {error ? <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert> : null}

        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: { xs: 3, sm: 4.5 },
            p: { xs: 1.7, sm: 3.1, md: 3.7 },
            color: "#fff",
            background: "linear-gradient(128deg, #19242F 0%, #2C4056 58%, #6957E8 100%)",
            boxShadow: "0 18px 48px rgba(25,36,47,.14)",
          }}
        >
          <Box sx={{ position: "absolute", width: 220, height: 220, borderRadius: "50%", bgcolor: "rgba(255,200,87,.12)", right: -80, top: -115 }} />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction="row" spacing={.7} alignItems="center">
              <AutoAwesomeRoundedIcon sx={{ color: BRAND.yellow, fontSize: 19 }} />
              <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: ".68rem", fontWeight: 1000, letterSpacing: .7 }}>
                TU PRIMERA ENTRADA
              </Typography>
            </Stack>
            <Typography sx={{ mt: .65, maxWidth: 720, fontSize: { xs: "1.45rem", sm: "2rem", md: "2.35rem" }, lineHeight: 1.08, fontWeight: 1000, letterSpacing: -.6 }}>
              {activeProject?.name || "Tu negocio"} ya tiene una dirección clara
            </Typography>
            <Typography sx={{ mt: .75, maxWidth: 720, color: "rgba(255,255,255,.76)", fontSize: { xs: ".82rem", sm: ".94rem" }, lineHeight: 1.5 }}>
              Usamos lo que elegiste para marcar un primer paso. Podés avanzar ahora o entrar al panel completo.
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={{ xs: 1.15, sm: 1.6 }}>
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ height: "100%", borderRadius: 3.4, p: { xs: 1.45, sm: 2.1 }, border: "1px solid rgba(25,36,47,.08)", bgcolor: "#fff" }}>
              <Typography sx={{ color: "rgba(25,36,47,.52)", fontSize: ".68rem", fontWeight: 1000, letterSpacing: .45 }}>
                LO QUE CONFIGURASTE
              </Typography>
              <Typography sx={{ mt: .45, color: BRAND.dark, fontSize: { xs: "1rem", sm: "1.15rem" }, fontWeight: 1000 }}>
                {businessProfile?.label || "Negocio flexible"}
              </Typography>
              <Typography
                sx={{
                  display: { xs: "-webkit-box", sm: "block" },
                  mt: .35,
                  color: "rgba(25,36,47,.58)",
                  fontSize: ".76rem",
                  lineHeight: 1.4,
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: { xs: 1, sm: "unset" },
                  overflow: { xs: "hidden", sm: "visible" },
                }}
              >
                {businessProfile?.description || "No cerraste un rubro todavía; podés definirlo después sin perder funciones."}
              </Typography>
              <Stack direction="row" useFlexGap flexWrap="wrap" gap={.65} sx={{ mt: 1.25 }}>
                {(businessGoalLabels.length ? businessGoalLabels : ["Sin objetivos cerrados todavía"]).map((label) => (
                  <Chip
                    key={label}
                    size="small"
                    label={label}
                    sx={{ bgcolor: "rgba(105,87,232,.08)", color: BRAND.purple, fontWeight: 900 }}
                  />
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                borderRadius: 3.4,
                p: { xs: 1.45, sm: 2.1 },
                border: `1px solid ${firstPalette.border}`,
                background: `linear-gradient(145deg, #fff 0%, ${firstPalette.soft} 100%)`,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ width: 42, height: 42, borderRadius: 2.4, display: "grid", placeItems: "center", flexShrink: 0, bgcolor: "#fff", color: firstPalette.main }}>
                  {firstGuidedAction.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: "rgba(25,36,47,.52)", fontSize: ".68rem", fontWeight: 1000, letterSpacing: .45 }}>
                    TU PRIMER RESULTADO
                  </Typography>
                  <Typography sx={{ mt: .3, color: BRAND.dark, fontSize: { xs: "1.05rem", sm: "1.25rem" }, lineHeight: 1.15, fontWeight: 1000 }}>
                    {firstGuidedAction.result || firstGuidedAction.description}
                  </Typography>
                  <Typography sx={{ display: { xs: "none", sm: "block" }, mt: .55, color: "rgba(25,36,47,.62)", fontSize: ".78rem", lineHeight: 1.45 }}>
                    {firstGuidedAction.description}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={.8} sx={{ mt: 1.55 }}>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardRoundedIcon />}
                  onClick={firstGuidedAction.onClick}
                  sx={{ width: { xs: "100%", sm: "auto" }, minHeight: { xs: 46, sm: 36 }, borderRadius: 999, textTransform: "none", fontWeight: 1000, bgcolor: firstPalette.main, "&:hover": { bgcolor: firstPalette.main, filter: "brightness(.94)" } }}
                >
                  {firstGuidedAction.label}
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate(`${projectBase}/inicio`, { replace: true })}
                  sx={{ width: { xs: "100%", sm: "auto" }, minHeight: { xs: 44, sm: 36 }, borderRadius: 999, textTransform: "none", fontWeight: 950, color: BRAND.dark }}
                >
                  Ir al panel completo
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    );
  }

  return (
    <Stack spacing={{ xs: 1.35, sm: 2.1, md: 2.6 }} sx={{ minWidth: 0 }}>
      {error ? <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: 2.8, sm: 4.5 },
          p: { xs: 1.45, sm: 2.6, md: 3.25 },
          color: "#fff",
          background: "linear-gradient(125deg, #19242F 0%, #2C4056 54%, #6957E8 100%)",
          boxShadow: "0 18px 48px rgba(25,36,47,.14)",
        }}
      >
        <Box sx={{ position: "absolute", width: 210, height: 210, borderRadius: "50%", bgcolor: "rgba(255,200,87,.12)", right: -80, top: -115 }} />
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={{ xs: 1.1, sm: 1.6, md: 2 }} alignItems={{ xs: "stretch", sm: "center" }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={.65}>
                <AutoAwesomeRoundedIcon sx={{ color: BRAND.yellow, fontSize: { xs: 17, sm: 20 } }} />
                <Typography sx={{ color: "rgba(255,255,255,.65)", fontSize: { xs: ".61rem", sm: ".72rem" }, fontWeight: 950, letterSpacing: .65 }}>
                  {guidedStart ? "PUESTA EN MARCHA" : "PANEL OPERATIVO"}
                </Typography>
              </Stack>
              <Typography sx={{ mt: .38, fontSize: { xs: "1.2rem", sm: "1.95rem", md: "2.3rem" }, lineHeight: 1.08, fontWeight: 1000, letterSpacing: -.65 }}>
                {guidedStart
                  ? `Hola${user?.name ? ` ${user.name}` : ""}, terminemos de preparar ${activeProject?.name || "tu negocio"}`
                  : `Hola${user?.name ? ` ${user.name}` : ""}, así viene ${activeProject?.name || "tu negocio"}`}
              </Typography>
              <Typography sx={{ display: { xs: "none", sm: "block" }, mt: .65, maxWidth: 700, color: "rgba(255,255,255,.72)", fontSize: ".91rem", lineHeight: 1.5 }}>
                {guidedStart
                  ? "Te mostramos sólo lo esencial hasta que el negocio tenga su base real lista."
                  : "Actividad, tareas y accesos de uso diario en un mismo lugar. La guía sigue disponible cuando la necesites."}
              </Typography>
            </Box>

            <Box sx={{ width: { xs: "100%", sm: 245, md: 285 }, flexShrink: 0 }}>
              <Typography sx={{ display: { xs: "none", sm: "block" }, mb: .5, color: "rgba(255,255,255,.62)", fontSize: ".67rem", fontWeight: 900 }}>PROYECTO ACTIVO</Typography>
              <Select
                fullWidth
                size="small"
                value={projectIdentifier(activeProject)}
                onChange={(event) => setActiveProject(event.target.value)}
                sx={{
                  minHeight: { xs: 44, sm: 40 },
                  bgcolor: "rgba(255,255,255,.11)",
                  color: "#fff",
                  borderRadius: 2.1,
                  fontSize: { xs: ".73rem", sm: ".86rem" },
                  fontWeight: 900,
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.2)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,.42)" },
                  ".MuiSvgIcon-root": { color: "#fff" },
                  ".MuiSelect-select": { py: { xs: .8, sm: 1 } },
                }}
              >
                {projects.map((project) => (
                  <MenuItem key={project.id || project._id || project.slug} value={projectIdentifier(project)}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
              <Stack direction="row" spacing={.6} useFlexGap flexWrap="wrap" sx={{ display: { xs: "none", sm: "flex" }, mt: .75 }}>
                <Chip size="small" label={billingAccessLabel(billingAccess)} sx={{ bgcolor: "rgba(255,255,255,.12)", color: "#fff", fontWeight: 850 }} />
                {modules.system ? <Chip size="small" label="Gestión activa" sx={{ bgcolor: "rgba(38,162,105,.24)", color: "#fff", fontWeight: 850 }} /> : null}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {operationallyLocked ? (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" startIcon={<WorkspacePremiumRoundedIcon />} onClick={() => navigate(subscribePath)} sx={{ fontWeight: 950 }}>
              Reactivar
            </Button>
          }
          sx={{ borderRadius: 3 }}
        >
          La página sigue disponible, pero las funciones operativas están pausadas.
        </Alert>
      ) : null}

      <MobilePriorityAction action={mobilePriorityAction} />

      {!guidedStart ? (
        <Stack spacing={{ xs: 1.2, sm: 1.7 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
            <Box>
              <Typography sx={{ color: BRAND.dark, fontSize: { xs: "1rem", sm: "1.3rem" }, fontWeight: 1000 }}>Resumen de los últimos 7 días</Typography>
              <Typography sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.55)", fontSize: ".78rem" }}>Datos reales de actividad web y del panel para este proyecto.</Typography>
            </Box>
            {experience.helpAvailable ? (
              <Button
                size="small"
                startIcon={<HelpOutlineRoundedIcon />}
                onClick={() => setShowGuidance((current) => !current)}
                sx={{ alignSelf: { xs: "flex-start", sm: "center" }, textTransform: "none", borderRadius: 999, fontWeight: 950, color: BRAND.dark }}
              >
                {showGuidance ? "Ocultar guía" : "Necesito ayuda"}
              </Button>
            ) : null}
          </Stack>

          <Grid container spacing={{ xs: .85, sm: 1.2 }}>
            <Grid item xs={6} md={3}><DashboardMetric label="Actividad" value={activityMetricValue} helper="Eventos web y operativos" icon={<HistoryRoundedIcon sx={{ fontSize: 19 }} />} /></Grid>
            <Grid item xs={6} md={3}><DashboardMetric label="Visitantes" value={visitorMetricValue} helper="Personas únicas detectadas" icon={<PublicRoundedIcon sx={{ fontSize: 19 }} />} /></Grid>
            <Grid item xs={6} md={3}><DashboardMetric label="Alta intención" value={intentMetricValue} helper="Acciones comerciales" icon={<ArrowForwardRoundedIcon sx={{ fontSize: 19 }} />} /></Grid>
            <Grid item xs={6} md={3}><DashboardMetric label="Equipo activo" value={teamMetricValue} helper="Usuarios del panel" icon={<GroupRoundedIcon sx={{ fontSize: 19 }} />} /></Grid>
          </Grid>

          <Grid container spacing={{ xs: 1, sm: 1.4 }}>
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ height: "100%", p: { xs: 1.3, sm: 1.8 }, borderRadius: 3.5, border: "1px solid rgba(25,36,47,.08)" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Box>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 1000 }}>Actividad reciente</Typography>
                    <Typography sx={{ color: "rgba(25,36,47,.52)", fontSize: ".72rem" }}>Lo último que pasó en el negocio.</Typography>
                  </Box>
                  <Button size="small" onClick={() => go(`${projectBase}/actividad`)} sx={{ textTransform: "none", fontWeight: 900 }}>Ver historial</Button>
                </Stack>
                <Stack spacing={.65}>
                  {recentActivity.slice(0, 4).map((event) => (
                    <Box key={event.id || event._id} sx={{ p: 1, borderRadius: 2.2, bgcolor: "rgba(25,36,47,.025)" }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ color: BRAND.dark, fontSize: ".8rem", fontWeight: 900 }}>{event.title || "Actividad registrada"}</Typography>
                          <Typography noWrap sx={{ color: "rgba(25,36,47,.5)", fontSize: ".68rem" }}>{event.description || event.action || ""}</Typography>
                        </Box>
                        <Typography sx={{ flexShrink: 0, color: "rgba(25,36,47,.42)", fontSize: ".64rem", fontWeight: 800 }}>{relativeTime(event.occurredAt || event.createdAt)}</Typography>
                      </Stack>
                    </Box>
                  ))}
                  {!activityLoading && !recentActivity.length ? (
                    <Typography sx={{ py: 1.2, color: "rgba(25,36,47,.52)", fontSize: ".76rem" }}>Todavía no hay actividad registrada. Las primeras acciones van a aparecer acá.</Typography>
                  ) : null}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ height: "100%", p: { xs: 1.3, sm: 1.8 }, borderRadius: 3.5, border: "1px solid rgba(25,36,47,.08)" }}>
                <Stack direction="row" spacing={.7} alignItems="center" sx={{ mb: 1 }}>
                  <TaskAltRoundedIcon sx={{ color: operationalTasks.length ? BRAND.orange : BRAND.green, fontSize: 20 }} />
                  <Box>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 1000 }}>{operationalTasks.length ? "Tareas pendientes" : "Todo al día"}</Typography>
                    <Typography sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.52)", fontSize: ".72rem" }}>Pendientes detectados por el estado real del proyecto.</Typography>
                  </Box>
                </Stack>
                <Stack spacing={.7}>
                  {operationalTasks.map((task) => (
                    <ButtonBase
                      key={task.id}
                      onClick={() => go(task.path, { operational: isOperationalDestination(task.path) })}
                      sx={{ display: "block", width: "100%", minHeight: { xs: 52, sm: 0 }, textAlign: "left", p: { xs: 1.05, sm: 1 }, borderRadius: 2.2, bgcolor: "rgba(243,90,58,.045)", touchAction: "manipulation" }}
                    >
                      <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".8rem", sm: ".78rem" }, fontWeight: 950 }}>{task.label}</Typography>
                      <Typography
                        sx={{
                          mt: .15,
                          color: "rgba(25,36,47,.52)",
                          fontSize: ".66rem",
                          lineHeight: 1.35,
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: { xs: 1, sm: "unset" },
                          overflow: { xs: "hidden", sm: "visible" },
                        }}
                      >
                        {task.description}
                      </Typography>
                    </ButtonBase>
                  ))}
                  {!operationalTasks.length ? (
                    <Box sx={{ p: 1, borderRadius: 2.2, bgcolor: "rgba(38,162,105,.06)" }}>
                      <Typography sx={{ color: BRAND.dark, fontSize: ".78rem", fontWeight: 900 }}>No hay pasos obligatorios pendientes.</Typography>
                      <Typography sx={{ display: { xs: "none", sm: "block" }, mt: .15, color: "rgba(25,36,47,.52)", fontSize: ".66rem" }}>Podés seguir operando normalmente y volver a la guía cuando quieras.</Typography>
                    </Box>
                  ) : null}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      ) : null}

      {readinessAvailable && (guidedStart || showGuidance || keepRoadmapVisible) ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            border: "1px solid rgba(25,36,47,.09)",
            bgcolor: "#fff",
            p: { xs: 1.25, sm: 2, md: 2.35 },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={1}
            sx={{ mb: roadmapComplete ? 0 : { xs: 1, sm: 1.35 } }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={.65} alignItems="center" useFlexGap flexWrap="wrap">
                <AutoAwesomeRoundedIcon sx={{ color: BRAND.orange, fontSize: 20 }} />
                <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".98rem", sm: "1.25rem" }, fontWeight: 1000 }}>
                  {roadmapComplete
                    ? "Roadmap completado"
                    : guidedStart
                      ? "Tu próximo resultado útil"
                      : "Roadmap del proyecto"}
                </Typography>
                {readinessLoading ? (
                  <Chip size="small" label="Actualizando" variant="outlined" sx={{ height: 22, fontSize: ".62rem", fontWeight: 900 }} />
                ) : null}
                {!roadmapComplete ? (
                  <ContextualHelp
                    compact
                    title="Cómo avanza esta guía"
                    text="Los pasos salen del estado real del proyecto, sus módulos y capacidades. El primero es la recomendación actual y el recorrido se actualiza al completar resultados."
                    bullets={[
                      `La base ${Number(readiness?.score || 0)}% resume preparación inicial, no rendimiento del negocio.`,
                      "Se muestran como máximo tres pasos para no convertir Inicio en una lista extensa.",
                    ]}
                    buttonSx={{ ml: { xs: "auto", sm: .25 } }}
                  />
                ) : null}
              </Stack>
              <Typography sx={{ mt: .3, color: "rgba(25,36,47,.58)", fontSize: { xs: ".72rem", sm: ".82rem" }, lineHeight: 1.4 }}>
                {roadmapComplete
                  ? "La preparación inicial está al día."
                  : "Empezá por el primer paso; los demás se actualizan al avanzar."}
              </Typography>
            </Box>
            <Stack direction="row" spacing={.7} alignItems="center" sx={{ flexShrink: 0 }}>
              {!roadmapComplete ? (
                <Chip
                  size="small"
                  label={`${projectRoadmap.length} ${projectRoadmap.length === 1 ? "próximo" : "próximos"}`}
                  sx={{ bgcolor: "rgba(38,162,105,.09)", color: BRAND.green, fontWeight: 950 }}
                />
              ) : null}
              <Chip
                size="small"
                label={`Base ${Number(readiness?.score || 0)}%`}
                sx={{ bgcolor: "rgba(105,87,232,.09)", color: BRAND.purple, fontWeight: 950 }}
              />
            </Stack>
          </Stack>

          {!roadmapComplete ? (
            <Stack spacing={.75}>
              {pendingRoadmapSteps.map((step, index) => {
                const palette = COLOR_MAP[step.color] || COLOR_MAP.orange;
                const isNext = index === 0;
                const stepDisabled = step.operational === true && operationallyLocked;
                return (
                  <ButtonBase
                    key={step.id || step.code}
                    onClick={() => go(step.path, { operational: step.operational === true })}
                    sx={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      borderRadius: 2.5,
                      "&:focus-visible": { boxShadow: `0 0 0 4px ${palette.soft}` },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: .85, sm: 1.15 },
                        p: { xs: 1, sm: 1.2 },
                        borderRadius: 2.5,
                        border: `1px solid ${isNext ? palette.border : "rgba(25,36,47,.08)"}`,
                        bgcolor: isNext ? palette.soft : "rgba(25,36,47,.018)",
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          bgcolor: isNext ? "#fff" : "rgba(25,36,47,.06)",
                          color: isNext ? palette.main : "rgba(25,36,47,.55)",
                          fontSize: ".72rem",
                          fontWeight: 1000,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={.65} alignItems="center" useFlexGap flexWrap="wrap">
                          <Typography sx={{ color: BRAND.dark, fontWeight: 1000, fontSize: { xs: ".82rem", sm: ".92rem" } }}>
                            {step.title}
                          </Typography>
                          {isNext ? (
                            <Chip size="small" label="SIGUIENTE" sx={{ height: 20, bgcolor: "#fff", color: palette.main, fontSize: ".56rem", fontWeight: 1000 }} />
                          ) : null}
                        </Stack>
                        <Typography
                          sx={{
                            display: { xs: isNext ? "-webkit-box" : "none", sm: "block" },
                            mt: .2,
                            color: "rgba(25,36,47,.58)",
                            fontSize: { xs: ".68rem", sm: ".76rem" },
                            lineHeight: 1.35,
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: { xs: 1, sm: "unset" },
                            overflow: { xs: "hidden", sm: "visible" },
                          }}
                        >
                          {step.description}
                        </Typography>
                      </Box>
                      <Stack alignItems="flex-end" spacing={.2} sx={{ flexShrink: 0 }}>
                        {stepDisabled ? <LockClockRoundedIcon sx={{ fontSize: 18, color: "rgba(25,36,47,.45)" }} /> : <ArrowForwardRoundedIcon sx={{ fontSize: 20, color: palette.main }} />}
                        <Typography sx={{ display: { xs: "none", sm: "block" }, color: stepDisabled ? "rgba(25,36,47,.48)" : palette.main, fontSize: ".64rem", fontWeight: 950 }}>
                          {stepDisabled ? "Ver acceso" : step.label}
                        </Typography>
                      </Stack>
                    </Box>
                  </ButtonBase>
                );
              })}
              {hiddenRoadmapCount > 0 ? (
                <Typography sx={{ px: .4, pt: .2, color: "rgba(25,36,47,.46)", fontSize: ".66rem", fontWeight: 800 }}>
                  Quedan {hiddenRoadmapCount} {hiddenRoadmapCount === 1 ? "paso" : "pasos"} más; se mostrarán al avanzar.
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Paper>
      ) : null}

      {membershipsEnabled && !guidedStart ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            border: "1px solid rgba(105,87,232,.16)",
            bgcolor: "rgba(105,87,232,.035)",
            p: { xs: 1.15, sm: 2, md: 2.4 },
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mb: { xs: .9, sm: 1.25 }, px: { xs: .15, sm: .2 } }}>
            <Box>
              <Stack direction="row" spacing={.7} alignItems="center">
                <CardMembershipRoundedIcon sx={{ color: BRAND.purple, fontSize: 20 }} />
                <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".96rem", sm: "1.28rem" }, fontWeight: 1000 }}>Membresías en recepción</Typography>
              </Stack>
              <Typography sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.58)", fontSize: ".82rem" }}>
                Accesos priorizados por la capacidad activa. Catálogo, productos, caja y demás herramientas siguen disponibles abajo.
              </Typography>
            </Box>
            <Chip size="small" label="Membresías activas" sx={{ alignSelf: { xs: "flex-start", sm: "center" }, bgcolor: "rgba(105,87,232,.10)", color: BRAND.purple, fontWeight: 900 }} />
          </Stack>

          <Grid container spacing={{ xs: .9, sm: 1.45 }}>
            {membershipActions.map((action) => (
              <Grid item xs={12} sm={6} md={3} key={action.key}>
                <ActionTile {...action} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : null}

      {(!guidedStart || !readinessAvailable) ? (
      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: 3, sm: 4 },
          border: "1px solid rgba(25,36,47,.08)",
          bgcolor: "#F9FAFD",
          p: { xs: 1.15, sm: 2, md: 2.4 },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="end" spacing={1} sx={{ mb: { xs: .9, sm: 1.25 }, px: { xs: .15, sm: .2 } }}>
          <Box>
            <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".96rem", sm: "1.28rem" }, fontWeight: 1000 }}>
              {guidedStart ? "Para empezar" : "Acciones rápidas"}
            </Typography>
            <Typography sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.58)", fontSize: ".82rem" }}>
              {guidedStart
                ? "Priorizamos estas herramientas según lo que elegiste al crear el negocio."
                : "Entrá directamente al formulario que necesitás."}
            </Typography>
          </Box>
          <Chip size="small" label={`${featuredActions.length} accesos`} variant="outlined" sx={{ display: { xs: "none", sm: "inline-flex" }, bgcolor: "#fff", fontWeight: 900 }} />
        </Stack>

        <Grid container spacing={{ xs: .9, sm: 1.45 }}>
          {featuredActions.map((action, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={12 / Math.min(featuredActions.length, 4)}
              key={action.key}
            >
              <ActionTile {...action} featured />
            </Grid>
          ))}
        </Grid>
      </Paper>
      ) : null}

      {guidedStart && !showAllTools ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            border: "1px dashed rgba(25,36,47,.16)",
            bgcolor: "rgba(25,36,47,.025)",
            p: { xs: 1.4, sm: 1.8 },
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
            <Box>
              <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>El resto sigue disponible</Typography>
              <Typography sx={{ mt: .25, color: "rgba(25,36,47,.58)", fontSize: ".8rem" }}>
                Tu prueba mantiene disponibles las demás herramientas.
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowAllTools(true)}
              sx={{ width: { xs: "100%", sm: "auto" }, minHeight: { xs: 44, sm: 32 }, flexShrink: 0, borderRadius: 999, textTransform: "none", fontWeight: 950, color: BRAND.dark, borderColor: "rgba(25,36,47,.2)" }}
            >
              Ver todas las herramientas
            </Button>
          </Stack>
        </Paper>
      ) : (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: { xs: .85, sm: 1.1 }, px: .2 }}>
          <Box>
            <Typography sx={{ color: BRAND.dark, fontSize: { xs: ".96rem", sm: "1.25rem" }, fontWeight: 1000 }}>{guidedStart ? "Más herramientas" : "Todas las herramientas"}</Typography>
            <Typography sx={{ display: { xs: "none", sm: "block" }, color: "rgba(25,36,47,.58)", fontSize: ".82rem" }}>
              {guidedStart ? "Todo lo incluido en tu prueba sigue accesible acá." : "Este inicio reemplaza la navegación compleja del proyecto."}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => guidedStart ? setShowAllTools(false) : navigate("/dashboard/proyectos")}
            sx={{ minWidth: 0, px: 1, textTransform: "none", color: "rgba(25,36,47,.58)", fontSize: { xs: ".68rem", sm: ".78rem" }, fontWeight: 950 }}
          >
            {guidedStart ? "Ocultar" : "Cambiar proyecto"}
          </Button>
        </Stack>

        <Grid container spacing={{ xs: .85, sm: 1.25 }}>
          {managementActions.map((action) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={action.key}>
              <ActionTile {...action} />
            </Grid>
          ))}
        </Grid>
      </Box>
      )}
    </Stack>
  );
}
